import { Component, EventEmitter, Output, TemplateRef, ViewChild } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { Router } from '@angular/router'
import { TranslateService } from '@ngx-translate/core'

import { AuthService } from '@app/modules/auth/auth.service'
import { SnackBarService } from '@core/modules/snack-bar/snack-bar.service'

import { SosService } from '../sos.service'
import { SosAlert, SOS_MAP_QUERY } from '../sos-alert.model'
import { SosAcknowledgeDialogComponent } from '../sos-acknowledge-dialog/sos-acknowledge-dialog.component'

@Component({
  selector: 'lin-sos-list',
  templateUrl: './sos-list.component.html',
  styleUrls: ['./sos-list.component.scss'],
  standalone: false,
})
export class SosListComponent {
  @Output() closed = new EventEmitter<void>()

  @ViewChild(TemplateRef) templateRef: TemplateRef<unknown>

  get isClient(): boolean {
    return !!this.authService.getCurrentUser()?.isClient()
  }

  constructor(
    public readonly sosService: SosService,
    private readonly dialog: MatDialog,
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly snackBar: SnackBarService,
    private readonly translate: TranslateService,
  ) {}

  trackById(_: number, alert: SosAlert): number {
    return alert.id
  }

  viewOnMap(alert: SosAlert): void {
    this.closed.emit()

    const user = this.authService.getCurrentUser()
    if (!user?.isClient()) {
      this.viewInEventsLog(alert, user?.isReseller() ? 'reseller' : 'admin')
      return
    }

    const hasCoords = alert.lat != null && alert.lng != null && +alert.lat !== 0 && +alert.lng !== 0
    if (alert.vehicleId == null && !hasCoords) {
      this.notify('SOS.UNAVAILABLE')
      return
    }

    const queryParams: Record<string, number> = {}
    if (alert.vehicleId != null) queryParams[SOS_MAP_QUERY.vehicleId] = alert.vehicleId
    if (hasCoords) {
      queryParams[SOS_MAP_QUERY.lat] = +alert.lat!
      queryParams[SOS_MAP_QUERY.lng] = +alert.lng!
    }

    // Keep a v2-shell client on the v2 map (John: "View on map" was dropping into the v1 UI). The v2 map
    // subclasses the v1 canvas, so it inherits the same SOS deep-link handler that reads these params.
    const mapPath = this.router.url.includes('/v2/') ? '/v2/client/map' : '/client/map'
    void this.router.navigate([mapPath], { queryParams })
  }

  openAcknowledge(alert: SosAlert): void {
    this.dialog
      .open(SosAcknowledgeDialogComponent, { data: alert, width: '440px', autoFocus: false, panelClass: 'sos-ack-dialog' })
      .afterClosed()
      .subscribe((result?: { note: string }) => {
        if (result) this.sosService.acknowledge(alert.id, result.note)
      })
  }

  private viewInEventsLog(alert: SosAlert, root: 'admin' | 'reseller'): void {
    if (!alert.eventId) {
      this.notify('SOS.UNAVAILABLE')
      return
    }
    const occurrenceTime = new Date(alert.occurrenceTime).getTime()
    const url = `/${root}/alerts/events-log-details/events-log/${alert.eventId}?eventTeam=${alert.eventTeam ?? ''}&occurrenceTime=${occurrenceTime}&eventLogId=${alert.eventLogId ?? ''}`
    void this.router.navigateByUrl(url)
  }

  private notify(key: string): void {
    this.snackBar.notificationArrived(this.translate.instant(key) as string)
  }
}
